<template>
  <section class="lcai">
    <header class="lcai-head">
      <div>
        <DetailLabel tag="h4">Références du même métier</DetailLabel>
        <p class="lcai-hint">
          L’IA compare le métier réel de nos clients à celui de ce prospect, et ne garde que les plus proches.
        </p>
      </div>
      <Button
        variant="secondary"
        size="sm"
        icon="fas fa-wand-magic-sparkles"
        :loading="loading"
        data-testid="linked-companies-ai-search"
        @click="$emit('search')"
      >
        {{ loading ? 'Recherche…' : (searched ? 'Relancer' : 'Chercher avec l’IA') }}
      </Button>
    </header>

    <InlineNotice v-if="error" variant="danger" icon="fas fa-circle-exclamation">{{ error }}</InlineNotice>

    <template v-else-if="searched && !loading">
      <LinkedCompaniesGroup v-if="companies.length > 0" :companies="companies" />
      <DetailEmpty v-else tag="p">Aucun de nos clients ne fait un métier assez proche pour servir de référence.</DetailEmpty>
    </template>
  </section>
</template>

<script setup>
/**
 * « Chercher avec l'IA » — les références du même métier, à la demande.
 *
 * Le modèle note les entreprises déjà clientes d'Alertis sur leur métier réel
 * (et non sur leur code NAF) ; seules les très proches reviennent, chacune
 * avec sa note et la phrase qui dit pourquoi. Rien ne part tant qu'on n'a pas
 * appuyé : c'est un service payant, et la plupart des fiches n'en ont pas
 * besoin.
 *
 * PRÉSENTATIONNEL : l'état vient de `useSimilarCompaniesAi`, `search` sort.
 */
import { Button, InlineNotice } from '../ui'
import DetailEmpty from '../data/DetailEmpty.vue'
import DetailLabel from '../data/DetailLabel.vue'
import LinkedCompaniesGroup from './LinkedCompaniesGroup.vue'

defineProps({
  companies: { type: Array, default: () => [] },
  loading: { type: Boolean, default: false },
  searched: { type: Boolean, default: false },
  error: { type: String, default: null }
})

defineEmits(['search'])
</script>

<style scoped>
.lcai {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-3);
}

.lcai-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--spacing-3);
  border-bottom: 1px solid var(--color-border-light);
  padding-bottom: var(--spacing-2);
}

.lcai-hint {
  margin: 0;
  font-size: var(--font-size-xs);
  color: var(--color-text-tertiary);
}

@media (max-width: 768px) {
  .lcai-head {
    flex-direction: column;
  }
}
</style>
